<template>
  <div>
    <v-alert
      v-model="showAlert"
      color="error"
      icon="error"
      :style="{'z-index': 500, position: 'fixed', width: '100%'}"
      dismissible
    >
      {{ alertMessage }}
    </v-alert>
    <v-toolbar dark color="primary">
      <v-toolbar-title v-show="$vuetify.breakpoint.mdAndUp"><span v-t="'externalProvider.devbridgeLunch'" /></v-toolbar-title>
      <v-spacer v-show="$vuetify.breakpoint.mdAndUp" />
      <v-toolbar-title v-if="externalProvider">{{ externalProvider.providerName }}</v-toolbar-title>
      <v-toolbar-title v-if="externalProvider">{{ externalProvider.selectedWeek }}</v-toolbar-title>
    </v-toolbar>
    <div v-if="savedData.length > 0">
      <div>
        <v-flex
          v-for="day in savedData"
          :key="day.id"
          :xs7="!printing"
          :xs12="printing"
          style="margin: auto; float:none;"
          :class="!printing ? 'py-3 px-1': 'py-3'"
        >
          <saved-day-card
            :day-name="$t(dayTitles[day.dayId])"
            :soups="day.soups"
            :mains="day.mains"
          />
        </v-flex>
      </div>
    </div>
    <div v-else-if="externalProvider" :class="$vuetify.breakpoint.smAndDown ? '' : 'ma-3'">
      <v-stepper v-model="stepper">
        <v-stepper-header style="height: auto">
          <template v-for="day in days">
            <v-stepper-step
              :key="`${day.id}-step`"
              :step="day.id"
              :complete="stepper > day.id && day.valid"
              editable
              :rules="[() => day.valid]"
            >
              <span v-t="dayTitles[day.id]" />
              <span> {{ calculateDay(day) }} </span>
              <small>{{ dayItemsCount(day) }}</small>
            </v-stepper-step>
            <v-divider v-if="day !== days[days.length - 1]" :key="day.id" />
          </template>
        </v-stepper-header>
        <v-stepper-items>
          <v-stepper-content
            v-for="day in days"
            :key="`${day.id}-content`"
            :step="day.id"
            :class="$vuetify.breakpoint.smAndDown ? 'px-1' : ''"
          >
            <h3 class="headline mb-3">
              <span v-t="dayDisplayNames[day.id]" /> <span v-t="'externalProvider.dishes'" />
              <v-menu v-if="otherDaysWithItems(day).length > 0" offset-y>
                <v-btn slot="activator" flat color="primary" :class="$vuetify.breakpoint.smAndDown ? 'no-margin-button' : ''">
                  <v-icon small class="pr-1">content_copy</v-icon>
                  <span v-t="'externalProvider.copyFromOtherDay'" />
                </v-btn>
                <v-list>
                  <v-list-tile v-for="dayToCopyFrom in otherDaysWithItems(day)" :key="dayToCopyFrom.id" @click="copyDishes(dayToCopyFrom, day)">
                    <v-list-tile-title><span v-t="dayDisplayNames[dayToCopyFrom.id]" /></v-list-tile-title>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </h3>
            <v-form :ref="`form-${day.id}`" v-model="formValid" lazy-validation>
              <v-layout wrap>
                <category-display :dishes="day.soups" @dishesChange="dishes => dishesUpdate(day.id, 'soups', dishes)">
                  <h3 slot="header"><v-icon class="pr-2">mdi-bowl</v-icon>{{ soupsTitle(day) }}</h3>
                </category-display>
                <category-display :dishes="day.mains" @dishesChange="dishes => dishesUpdate(day.id, 'mains', dishes)">
                  <h3 slot="header"><v-icon class="pr-2">restaurant</v-icon>{{ mainsTitle(day) }}</h3>
                </category-display>
              </v-layout>
            </v-form>
            <v-btn v-if="!isFirstDay(day)" flat color="primary" @click="previousDay(day)">
              <v-icon small class="pr-1">arrow_back</v-icon>
              <span v-t="'externalProvider.previousDay'" />
            </v-btn>
            <v-btn v-if="isLastDay(day)" color="primary" :loading="saving" @click="save()">
              <span v-t="'externalProvider.submit'" />
              <v-icon class="pl-1 pb-1">check</v-icon>
            </v-btn>
            <v-btn v-if="!isLastDay(day)" color="primary" @click="nextDay(day)">
              <span v-t="'externalProvider.nextDay'" />
              <v-icon small class="pl-1">arrow_forward</v-icon>
            </v-btn>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>
    </div>
    <div v-else-if="loading > 0" class="fit-content" style="margin: auto; padding-top: 100px;">
      <v-progress-circular indeterminate :size="100" :width="6" color="primary" />
    </div>
    <div v-else :style="{color: 'rgba(0,0,0,.54)', display: 'flex', 'flex-direction': 'column', margin: 'auto', 'align-items': 'center'}">
      <v-icon :style="{'font-size': '13rem'}">visibility_off</v-icon>
      <br>
      <div :style="{'font-size': '3rem'}"><span v-t="'externalProvider.nothingFound'" /></div>
      <div :style="{'font-size': '1.5rem'}"><span v-t="'externalProvider.nothingFoundBody'" /></div>
    </div>
    <v-btn
      v-if="!printing && savedData.length > 0"
      fab
      fixed
      right
      bottom
      color="primary"
      @click="print"
    >
      <v-icon>print</v-icon>
    </v-btn>
  </div>
</template>

<script>
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import ErrorHandler from '@/services/ErrorHandler';
import gql from 'graphql-tag';
import CategoryDisplay from './CategoryDisplay.vue';
import SavedDayCard from './SavedDayCard.vue';

export default {
  name: 'ExternalProviderContainer',
  components: {
    'category-display': CategoryDisplay,
    'saved-day-card': SavedDayCard,
  },
  data: () => ({
    showAlert: false,
    saving: false,
    alertMessage: null,
    externalProvider: null,
    loading: 0,
    printing: false,
    providerName: 'Kavinė Kregždutė',
    stepper: null,
    input: null,
    formValid: true,
    savedData: [],
    dayDisplayNames: {
      1: 'externalProvider.monday',
      2: 'externalProvider.tuesday',
      3: 'externalProvider.wednesday',
      4: 'externalProvider.thursday',
      5: 'externalProvider.friday',
    },
    dayTitles: {
      1: 'common.monday',
      2: 'common.tuesday',
      3: 'common.wednesday',
      4: 'common.thursday',
      5: 'common.friday',
    },
    days: [{ id: 1, valid: true }],
  }),
  watch: {
    stepper(currentStep, previousStep) {
      this.validateDayForm(this.days[previousStep - 1]);
    },
  },
  mounted() {
    window.onbeforeprint = () => {
      this.printing = true;
    };
    window.onafterprint = () => {
      this.printing = false;
    };
  },
  methods: {
    soupsTitle(day) {
      const soupsCount = day.soups.filter(s => s.name && s.price).length;
      return soupsCount > 0 ? this.$t('externalProvider.soupsWithCount', { count: soupsCount }) : this.$t('externalProvider.soups');
    },
    mainsTitle(day) {
      const mainsCount = day.mains.filter(s => s.name && s.price).length;
      return mainsCount > 0 ? this.$t('externalProvider.mainsWithCount', { count: mainsCount }) : this.$t('externalProvider.mains');
    },
    dayItemsCount(day) {
      const count = day.soups.filter(s => s.name && s.price).length + day.mains.filter(s => s.name && s.price).length;
      return count > 0 ? this.$t('externalProvider.dishesWithCount', { count }) : this.$t('externalProvider.noDishes');
    },
    validateDayFormAlert(day) {
      const isDayValid = this.validateDayForm(day);
      if (!isDayValid) {
        this.showAlert = true;
        this.alertMessage = this.$t('externalProvider.formError');
      }
      return isDayValid;
    },
    validateDayForm(day) {
      const valid = this.$refs[`form-${day.id}`][0].validate();
      const result = valid && (day.soups.length > 0 || day.mains.length > 0);

      // eslint-disable-next-line no-param-reassign
      day.valid = result;

      return result;
    },
    nextDay(day) {
      const index = this.days.indexOf(day);
      this.stepper = this.days[index + 1].id;
    },
    previousDay(day) {
      const index = this.days.indexOf(day);
      this.stepper = this.days[index - 1].id;
    },
    dishesUpdate(dayId, type, newDishes) {
      this.days.find(d => d.id === dayId)[type] = newDishes;
    },
    isLastDay(day) {
      const index = this.days.indexOf(day);
      return index === this.days.length - 1;
    },
    isFirstDay(day) {
      const index = this.days.indexOf(day);
      return index === 0;
    },
    print() {
      this.printing = true;
      setTimeout(() => window.print());
    },
    calculateDay(day) {
      return moment(this.externalProvider.selectedWeek).add(day.id - 1, 'day').format('YYYY-MM-DD');
    },
    async save() {
      let allValid = true;
      this.days.forEach((d) => {
        if (!this.validateDayFormAlert(d)) {
          allValid = false;
          const day = d;
          day.valid = false;
        }
      });

      if (!allValid) {
        return;
      }

      const data = this.days.filter(d => d.soups.length > 0 || d.mains.length > 0).map(d => ({
        dayId: d.id,
        soups: d.soups.filter(s => !!s.price && !!s.name).map(s => ({ price: Number(s.price).toFixed(2), name: s.name.trim() })),
        mains: d.mains.filter(m => !!m.price && !!m.name).map(m => ({ price: Number(m.price).toFixed(2), name: m.name.trim() })),
      }));

      this.saving = true;
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ExternalProviderSaveMutation($data: [ExternalProviderData]!, $guid: String!, $timezoneOffset: Int!) {
              saveExternalProvider(data: $data, guid: $guid, timezoneOffset: $timezoneOffset)
            }`,
          variables: {
            data,
            guid: this.$route.params.guid,
            timezoneOffset: moment(this.externalProvider.selectedWeek).utcOffset(),
          },
        });
        this.savedData = data;
      } catch (e) {
        this.showAlert = true;
        this.alertMessage = this.$t('externalProvider.savingError');
      } finally {
        this.saving = false;
      }
    },
    otherDaysWithItems(day) {
      return this.days.filter(d => d.soups.length > 0 || d.mains.length > 0).filter(d => d !== day);
    },
    copyDishes(from, to) {
      to.soups = cloneDeep(from.soups); // eslint-disable-line no-param-reassign
      to.mains = cloneDeep(from.mains); // eslint-disable-line no-param-reassign
    },
  },
  apollo: {
    $loadingKey: 'loading',
    externalProvider: {
      fetchPolicy: 'network-only',
      query: gql`query ExternalProviderQuery($guid: String!) {
        externalProvider(guid: $guid) {
          providerName,
          selectedWeek,
          days
        }
      }`,
      variables() {
        return {
          guid: this.$route.params.guid,
        };
      },
      error(error) {
        this.externalProvider = null;
        ErrorHandler.handleApolloError(error);
      },
      result({ data }) {
        if (!data.externalProvider) {
          return;
        }
        this.days = data.externalProvider.days.map(d => ({
          id: d,
          soups: [],
          mains: [],
          valid: true,
        }));

        this.stepper = this.days[0].id;
      },
    },
  },
};
</script>

<style scoped>
.no-margin-button {
  margin-right: 0 !important;
  margin-left: 0 !important;
}

.no-margin-button >>> .btn__content {
  padding-left: 0 !important;
  padding-right: 0 !important;
}
</style>
